import { useState, useEffect } from "react";
import { LogIn, LayoutDashboard, Shield, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import LanguageToggle from "@/components/LanguageToggle";
import { useBranding } from "@/hooks/useBranding";

const Header = () => {
  const [memberName, setMemberName] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { isAdmin } = useAdminAuth();
  const branding = useBranding();

  useEffect(() => {
    const fetchMemberName = async () => {
      if (!user) {
        setMemberName(null);
        return;
      }
      const { data } = await supabase
        .from("members")
        .select("first_name")
        .eq("user_id", user.id)
        .maybeSingle();
      setMemberName(data?.first_name ?? null);
    };
    fetchMemberName();
  }, [user]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const getInitials = () => {
    if (memberName) return memberName.charAt(0).toUpperCase();
    if (user?.email) return user.email.charAt(0).toUpperCase();
    return "U";
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-card/95 backdrop-blur-md">
      <div className="container flex h-16 items-center justify-between md:h-20">
        <Link to="/" className="flex items-center gap-2.5" aria-label={`${branding.name} home`}>
          {branding.headerLogoUrl ? (
            <img
              src={branding.headerLogoUrl}
              alt={`${branding.name} logo`}
              className="h-9 w-auto max-w-44 object-contain md:h-10"
            />
          ) : (
            <>
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary font-bold text-primary-foreground md:h-10 md:w-10">
                {branding.initials}
              </span>
              <span className="text-xl font-bold text-foreground md:text-2xl">{branding.name}</span>
            </>
          )}
        </Link>

        <div className="flex items-center gap-2">
          <LanguageToggle />

          {user ? (
            <>
              <Avatar className="h-8 w-8">
                <AvatarFallback>{getInitials()}</AvatarFallback>
              </Avatar>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/dashboard">
                  <LayoutDashboard className="mr-1.5 h-4 w-4" />
                  Dashboard
                </Link>
              </Button>
              {isAdmin && (
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/admin">
                    <Shield className="mr-1.5 h-4 w-4" />
                    Admin
                  </Link>
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={handleSignOut}>
                <LogOut className="mr-1.5 h-4 w-4" />
                Sign out
              </Button>
            </>
          ) : (
            <Button size="sm" asChild>
              <Link to="/auth">
                <LogIn className="mr-1.5 h-4 w-4" />
                Sign in
              </Link>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
